import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumIcon from "@mui/icons-material/Forum";
import GroupsIcon from "@mui/icons-material/Groups";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MenuIcon from "@mui/icons-material/Menu";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import TvIcon from "@mui/icons-material/Tv";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/AuthContext";
import { getAdminRoleAccess } from "../../access/adminAccess";
import logo from "../../Assets/logo.png";

const iconMap = {
  "لوحة التحكم": <MenuIcon />,
  "المنيو": <RestaurantMenuIcon />,
  "الخامات": <Inventory2Icon />,
  "الطاولات": <TableRestaurantIcon />,
  "الفواتير": <ReceiptLongIcon />,
  "مشروبات AI": <LocalCafeIcon />,
  "بيانات العملاء": <GroupsIcon />,
  "العملاء": <GroupsIcon />,
  "المستخدمين": <ManageAccountsIcon />,
  "شاشات": <TvIcon />,
  "الشاشات": <TvIcon />,
  "تقارير محادثات agent": <ForumIcon />,
  "محادثات AI": <ForumIcon />,
  "احصائيات استهلاك الخامات": <AssessmentIcon />,
  "استخدام المواد": <AssessmentIcon />,
  "استخدام المواد Ai": <AssessmentIcon />,
  "تقارير الطلبات": <ReceiptLongIcon />,
  "نقطة البيع": <PointOfSaleIcon />,
};

const isItemActive = (pathname, itemPath) => {
  if (!itemPath) return false;
  if (itemPath === "/auth/pos-pages") return pathname === itemPath;
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
};

export default function Sidebar({
  drawerWidth,
  collapsedWidth = 64,
  open = true,
  mobileOpen = false,
  isMobile = false,
  onToggle,
  onCloseMobile,
  anchor = "left",
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userRole } = useAuth();
  const menuItems = getAdminRoleAccess(userRole)?.menu || [];
  const width = isMobile ? drawerWidth : open ? drawerWidth : collapsedWidth;

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) onCloseMobile?.();
  };

  const handleLogout = () => {
    if (isMobile) onCloseMobile?.();
    logout();
    navigate("/background/gate/login");
  };

  const drawerContent = (
    <Box
      dir="rtl"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "primary.main",
        color: "#fff",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={open || isMobile ? "space-between" : "center"}
        spacing={1}
        sx={{
          minHeight: { xs: 64, md: 72 },
          px: open || isMobile ? 1.5 : 1,
          borderBottom: `1px solid ${alpha("#fff", 0.16)}`,
        }}
      >
        {(open || isMobile) && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Box
              component="img"
              src={logo}
              alt="Nuvvy"
              sx={{
                width: 38,
                height: 38,
                objectFit: "contain",
                borderRadius: 1.5,
                bgcolor: alpha("#fff", 0.14),
                p: 0.35,
                flexShrink: 0,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
                Nuvvy
              </Typography>
              <Typography variant="caption" sx={{ color: alpha("#fff", 0.78), fontWeight: 700 }} noWrap>
                لوحة الإدارة
              </Typography>
            </Box>
          </Stack>
        )}

        <Tooltip title={isMobile ? "إغلاق القائمة" : "تبديل القائمة"}>
          <IconButton
            onClick={isMobile ? onCloseMobile : onToggle}
            aria-label={isMobile ? "close admin sidebar" : "toggle admin sidebar"}
            sx={{
              color: "#fff",
              border: `1px solid ${alpha("#fff", 0.22)}`,
              borderRadius: 2,
              bgcolor: alpha("#fff", 0.08),
              "&:hover": { bgcolor: alpha("#fff", 0.16) },
            }}
          >
            {isMobile ? <CloseRoundedIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1,
          py: 1.25,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha("#fff", 0.24),
            borderRadius: 99,
          },
        }}
      >
        <List sx={{ display: "grid", gap: 0.5 }}>
          {menuItems.map((item) => {
            const active = isItemActive(location.pathname, item.path);
            const button = (
              <ListItemButton
                key={item.path || item.label}
                selected={active}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  minHeight: 46,
                  borderRadius: 2,
                  px: open || isMobile ? 1.25 : 1,
                  justifyContent: open || isMobile ? "flex-start" : "center",
                  color: "#fff",
                  "&:hover": { bgcolor: alpha("#fff", 0.12) },
                  "&.Mui-selected": {
                    bgcolor: alpha("#fff", 0.2),
                    boxShadow: `inset 3px 0 0 ${theme.palette.success.light}`,
                    "&:hover": { bgcolor: alpha("#fff", 0.24) },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: open || isMobile ? 38 : 0,
                    justifyContent: "center",
                  }}
                >
                  {iconMap[item.label] || <MenuIcon />}
                </ListItemIcon>
                {(open || isMobile) && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: active ? 900 : 700,
                      noWrap: true,
                    }}
                  />
                )}
              </ListItemButton>
            );

            return open || isMobile ? (
              button
            ) : (
              <Tooltip key={item.path || item.label} title={item.label} placement="left">
                {button}
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ borderColor: alpha("#fff", 0.16) }} />

      <Box sx={{ p: 1 }}>
        <Tooltip title={open || isMobile ? "" : "تسجيل الخروج"} placement="left">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 44,
              borderRadius: 2,
              color: "#fff",
              justifyContent: open || isMobile ? "flex-start" : "center",
              bgcolor: alpha(theme.palette.error.light, 0.14),
              border: `1px solid ${alpha(theme.palette.error.light, 0.3)}`,
              "&:hover": { bgcolor: alpha(theme.palette.error.light, 0.22) },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: open || isMobile ? 38 : 0,
                justifyContent: "center",
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {(open || isMobile) && (
              <ListItemText
                primary="تسجيل الخروج"
                primaryTypographyProps={{ fontSize: 14, fontWeight: 800 }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor={anchor}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={onCloseMobile}
      ModalProps={{ keepMounted: true }}
      sx={{
        width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width,
          boxSizing: "border-box",
          border: 0,
          overflowX: "hidden",
          boxShadow: isMobile ? `0 18px 46px ${alpha(theme.palette.common.black, 0.26)}` : "none",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
